<template>
  <BittsDrawer
    :visible="true"
    :title="step === 'form' ? 'Create API Key' : 'API Key Created'"
    :primary-button-text="step === 'form' ? 'Create Key' : 'Done'"
    :secondary-button-text="step === 'form' ? 'Cancel' : ''"
    :is-saving="isCreating"
    :is-saving-disabled="step === 'form' && !canSubmit"
    @action:primary="handleAction"
    @action:secondary="handleClosed"
    @closed="handleClosed"
  >
    <template #content>
      <div v-if="step === 'form'" class="flex flex-col gap-16 p-24">
        <BittsInput
          v-model="keyName"
          name="api-key-name"
          placeholder="e.g. My Integration"
          :form-label="{ title: 'Key Name' }"
          :status="isNameTaken ? 'danger' : 'default'"
          danger-text="A key with that name already exists."
          data-testid="api-key-name-input"
        />

        <BittsAlert
          v-if="!availableScopes.length"
          color="warning"
          message="No API scopes are available for your account. Contact your administrator to create API keys."
        />
        <BittsSelectTags
          v-else
          v-model="selectedScopes"
          mode="multiple"
          :options="scopeOptions"
          :form-label="{ title: 'Scopes' }"
          placeholder="Select scopes"
          data-testid="api-key-scopes-select"
        />

        <ApiKeyCreateDrawerField
          v-slot="{ id }"
          label="Allowed IPs"
          :hint="ipsNote"
          :danger-hint="ipsWarning"
        >
          <BittsFilterTagInput
            :id="id"
            v-model="allowedIps"
            placeholder="Type an IP and press enter. Leave empty to allow any."
          />
        </ApiKeyCreateDrawerField>

        <ApiKeyCreateDrawerField
          v-slot="{ id }"
          label="Expiration Date"
          hint="Optional. Leave blank for no expiration."
        >
          <div class="flex items-center gap-8">
            <BittsRangePicker
              :id="id"
              use-single-date-picker
              :initial-date="expiresDate"
              format="YYYY-MM-DD"
              placeholder="No expiration"
              :disabled-date="disabledExpiryDate"
              @change="onExpiryChange"
            />
            <BittsButton
              v-if="expiresDate"
              type="neutral"
              variant="ghost"
              size="small"
              :center-icon="['fak', 'x']"
              aria-label="Clear expiration date"
              @click="expiresDate = ''"
            />
          </div>
        </ApiKeyCreateDrawerField>
      </div>

      <ApiKeyCreateDrawerResultStep v-else :raw-key="rawKey" />
    </template>
  </BittsDrawer>
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsButton,
  BittsDrawer,
  BittsFilterTagInput,
  BittsInput,
  BittsRangePicker,
  BittsSelectTags,
} from '@crossbeam/bitts';
import type { BittsRangePickerChangeValue } from '@crossbeam/bitts';

import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { computed, ref } from 'vue';

import ApiKeyCreateDrawerField from '@/components/ApiKeyCreateDrawerField.vue';
import ApiKeyCreateDrawerResultStep from '@/components/ApiKeyCreateDrawerResultStep.vue';

import { crossbeamApi } from '@/api';
import { useFlashesStore } from '@/stores';

const { invalidKeyNames = [], availableScopes = [] } = defineProps<{
  invalidKeyNames?: string[];
  availableScopes?: string[];
}>();

const emit = defineEmits<{
  closed: [];
  created: [];
}>();

const flashesStore = useFlashesStore();
const step = ref<'form' | 'result'>('form');
const keyName = ref('');
const selectedScopes = ref<string[]>([]);
const allowedIps = ref<string[]>([]);
// Keep the picker's value as plain YYYY-MM-DD so round-tripping through
// the date picker doesn't shift the day in non-UTC zones.
const expiresDate = ref('');
const rawKey = ref('');
const isCreating = ref(false);

const IP_REGEX =
  /^((25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)\.){3}(25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)$/;

const scopeOptions = computed(() =>
  availableScopes.map((scope) => ({ value: scope, label: scope })),
);

const invalidIps = computed(() =>
  allowedIps.value.filter((ip) => !IP_REGEX.test(ip)),
);
const ipsNote = computed(() =>
  allowedIps.value.length
    ? ''
    : 'No restriction. Requests accepted from any IP.',
);
const ipsWarning = computed(() =>
  invalidIps.value.length
    ? `Not a valid IPv4 address: ${invalidIps.value.join(', ')}`
    : '',
);

const isNameTaken = computed(() => {
  const name = keyName.value.trim().toLowerCase();
  return invalidKeyNames.some((n) => n.toLowerCase() === name);
});
const canSubmit = computed(
  () =>
    !!keyName.value.trim() &&
    selectedScopes.value.length > 0 &&
    !isNameTaken.value &&
    !invalidIps.value.length,
);

function disabledExpiryDate(current: Dayjs) {
  return current && current.isBefore(dayjs().add(1, 'day'), 'day');
}

function onExpiryChange(date: BittsRangePickerChangeValue) {
  if (Array.isArray(date)) return;
  expiresDate.value = date ? date.format('YYYY-MM-DD') : '';
}

// Pin expiration to end-of-day UTC. "Expires on June 15" should mean the
// key works through all of June 15; sending T23:59:59.999Z preserves that,
// where a bare T00:00:00Z would expire the key the instant the date begins.
const expiresAt = computed(() =>
  expiresDate.value ? `${expiresDate.value}T23:59:59.999Z` : '',
);

async function handleAction() {
  if (step.value === 'result') {
    emit('closed');
    return;
  }
  if (!canSubmit.value) return;
  isCreating.value = true;
  const { data, error } = await crossbeamApi.POST('/v0.1/api-keys', {
    body: {
      name: keyName.value.trim(),
      scopes: selectedScopes.value,
      ...(allowedIps.value.length ? { allowed_ips: allowedIps.value } : {}),
      ...(expiresAt.value ? { expires_at: expiresAt.value } : {}),
    },
  });
  if (error) {
    flashesStore.addErrorFlash({ message: 'Failed to create API key' });
    isCreating.value = false;
    return;
  }
  if (data) {
    rawKey.value = data.key.raw_key;
    emit('created');
    step.value = 'result';
  }
  isCreating.value = false;
}

function handleClosed() {
  emit('closed');
}
</script>
